// The jobs on the Fleet page's mock machines (#6745): what `GET /v1/fleet/utilization`
// would send as `boxes[].jobs` for each scenario row (`fleet-machine-mocks.ts`). Split out
// of that file for the mock corpus's 300-line ceiling (`mock-size.test.ts`).
//
// A working machine of THIS org holds one job by default: its task title, a Coder, working,
// its usage the machine's own reading (the one job IS the load), so a card never shows a
// job using more than its machine. A seed's `jobs` replaces that default: several jobs, a
// starting one, last-known jobs on an offline machine (`fleet-machine-seed.ts` `SeedJob`).
// Every usage number is a literal or the machine's own reading, never an estimate.
//
// The machine's jobs state (#6746) is the domain's fold over the same seeds, fed as their
// stored facts ({@link mockHeldJobs}) and the beat the row would carry ({@link mockJobsBeat}),
// so a fixture cannot show a jobs state the server would never produce.

import type {
  AgentModeKey,
  MachineHeldJob,
  MachineJobStatus,
  MachineJobsBeat,
  PromptRole,
} from '@developerz/domain';
import { GB, MIN, type Seed, type SeedJob } from './fleet-machine-seed';
import type { UtilJob, UtilJobEpic } from './fleetUtilization';

/** What a working machine is doing: the prototype's sample task titles. */
export const TASK_TITLES: readonly string[] = [
  'Split billing webhooks into a retry queue',
  'Add rate-limit headers to the public API',
  'Translate onboarding emails to Spanish',
  'Move email templates to MJML',
  'Write tests for the plan limits check',
  'Speed up the repo search index',
  'Add dark mode to the settings page',
  'Fix the timezone bug in weekly digests',
];

const REPO = 'developerz-ai/developerz.ai';

/**
 * The prototype's sample epics (`EPIC_POOL`, `:1331-1335`), as #6745 reports one on a job,
 * with #6753's pause mark and stored model: Billing unpinned (Automatic), SSO pinned. Never
 * the prototype's `MODELS` list: a pin is a `(provider, model)` an account's key serves.
 */
export const BILLING: UtilJobEpic = {
  plan_id: 'plan_mock_2910',
  title: 'Billing webhooks overhaul',
  done: 3,
  total: 7,
  running: 2,
  paused_at: null,
  llm: null,
};
export const SSO: UtilJobEpic = {
  plan_id: 'plan_mock_2933',
  title: 'SSO for the admin panel',
  done: 1,
  total: 5,
  running: 1,
  paused_at: null,
  llm: { provider: 'anthropic', model: 'claude-opus-4-8' },
};

/** The switch a role's own work rides (#6769), as `AGENT_MODE_KEY_FOR_ORIGIN` maps its origins. */
function modeKeyOfRole(role: PromptRole | null): AgentModeKey | null {
  if (role === 'worker') return 'worker';
  if (role === 'reviewer' || role === 'scout' || role === 'devops' || role === 'triage')
    return role;
  return null;
}

/** The stored status a seed job's row would carry. */
const statusOf = (s: SeedJob): MachineJobStatus =>
  s.status ?? (s.state === 'starting' ? 'assigned' : 'in_progress');

function job(s: SeedJob, nowMs: number): UtilJob {
  const u = s.usage;
  return {
    id: s.id,
    title: s.title,
    repo: s.repo ?? REPO,
    run_id: s.state === 'starting' ? null : `run_${s.id}`,
    status: statusOf(s),
    started_at:
      s.state === 'starting' ? null : new Date(nowMs - (s.startedMinAgo ?? 4) * MIN).toISOString(),
    class: 'coding',
    role: s.role,
    mode_key: s.modeKey === undefined ? modeKeyOfRole(s.role) : s.modeKey,
    state: s.state,
    pausable: s.role === 'worker',
    usage:
      u === undefined
        ? null
        : {
            cpu_pct: u[0],
            mem_bytes: u[1] === null ? null : Math.round(u[1] * GB),
            disk_bytes: u[2] === null ? null : Math.round(u[2] * GB),
          },
    epic: s.epic ?? null,
  };
}

/** The default job of a working own-org machine: one Coder, its usage the machine's own. */
function defaultJob(i: number, s: Seed): SeedJob {
  return {
    id: s.taskId ?? `tsk_scn${String(i).padStart(4, '0')}`,
    title: TASK_TITLES[i % TASK_TITLES.length] ?? 'Untitled',
    role: 'worker',
    state: 'working',
    // CPU and memory are the machine's own reading; the checkout size is a literal.
    usage: [Math.min(s.cpu, 100), s.memGb, 1.8],
  };
}

/** A scenario row's job seeds: none on another org's machine, the seed's, or the default. */
function seedJobs(i: number, s: Seed): readonly SeedJob[] {
  if (s.viaOrg !== undefined) return [];
  return s.jobs ?? (s.status === 'working' ? [defaultJob(i, s)] : []);
}

/** A scenario row's `jobs`. */
export function mockJobs(i: number, s: Seed, nowMs: number): readonly UtilJob[] {
  return seedJobs(i, s).map((j) => job(j, nowMs));
}

/** The row's jobs as the held-jobs read would return their stored facts (#6746). */
export function mockHeldJobs(i: number, s: Seed, nowMs: number): MachineHeldJob[] {
  return seedJobs(i, s).map((j) => ({
    id: j.id,
    status: statusOf(j),
    cancelRequested: j.cancelRequested ?? false,
    pauseRequested: j.state === 'pausing',
    updatedAt: new Date(nowMs - (j.silentMin ?? 1) * MIN),
    lastProgressAt: null,
  }));
}

/** The beat the row would carry: the jobs it reports running, none on an older runner. */
export function mockJobsBeat(i: number, s: Seed, live: boolean): MachineJobsBeat {
  if (!live) return { kind: 'absent' };
  if (s.reportsJobUsage === false) return { kind: 'live', taskIds: null };
  const running = seedJobs(i, s).filter((j) => j.state === 'working' || j.state === 'pausing');
  return { kind: 'live', taskIds: running.map((j) => j.id) };
}

/** The 6-machine set's "agents": a Coder in an epic and a Reviewer, both working. */
export const AGENTS_JOBS: readonly SeedJob[] = [
  {
    id: 'tsk_mock_agents_a',
    title: 'Split billing webhooks into a retry queue',
    role: 'worker',
    state: 'working',
    usage: [31, 2.4, 3.1],
    epic: BILLING,
    startedMinAgo: 12,
  },
  {
    id: 'tsk_mock_agents_b',
    title: 'Reviewing PR #6474',
    role: 'reviewer',
    state: 'working',
    usage: [9, 0.8, 1.2],
    startedMinAgo: 6,
  },
];

/** The 20-machine set's fleet-1: two jobs, one just claimed (Starting, "Measuring usage…"). */
export const TWO_JOBS_ONE_STARTING: readonly SeedJob[] = [
  {
    id: 'tsk_mock_two_a',
    title: 'Add rate-limit headers to the public API',
    role: 'worker',
    state: 'working',
    usage: [58, 4.2, 6.4],
    epic: SSO,
    startedMinAgo: 18,
  },
  { id: 'tsk_mock_two_b', title: 'Reviewing PR #418', role: 'reviewer', state: 'starting' },
];

/** The 20-machine set's fleet-8: two Coders of one epic (one group, summed) and a DevOps job. */
export const THREE_JOBS: readonly SeedJob[] = [
  {
    id: 'tsk_mock_three_a',
    title: 'Move email templates to MJML',
    role: 'worker',
    state: 'working',
    usage: [14, 1.1, 2.2],
    epic: BILLING,
    startedMinAgo: 25,
  },
  {
    id: 'tsk_mock_three_b',
    title: 'Write tests for the plan limits check',
    role: 'worker',
    // A first sample: no CPU share yet, so it reads "-" and is left out of the sum.
    state: 'working',
    usage: [null, 0.6, 0.4],
    epic: BILLING,
    startedMinAgo: 1,
  },
  {
    id: 'tsk_mock_three_c',
    title: 'Fix the staging deploy',
    role: 'devops',
    state: 'working',
    usage: [18, 1.4, 3.1],
    startedMinAgo: 9,
  },
];

/** The 20-machine set's fleet-12: a runner older than #6743 (no per-job usage). */
export const OLD_RUNNER_JOBS: readonly SeedJob[] = [
  {
    id: 'tsk_mock_old_runner',
    title: 'Speed up the repo search index',
    role: 'worker',
    state: 'working',
    startedMinAgo: 40,
  },
];

/** The 20-machine set's fleet-16: a job that has made no progress for 75 minutes (#6746). */
export const STUCK_JOB: readonly SeedJob[] = [
  {
    id: 'tsk_mock_stuck',
    title: 'Fix the timezone bug in weekly digests',
    role: 'worker',
    state: 'working',
    usage: [2, 0.9, 1.6],
    startedMinAgo: 140,
    silentMin: 75,
  },
];

/** The 20-machine set's fleet-15: a job told to stop whose box has not ended it yet (#6746). */
export const STOPPING_JOB: readonly SeedJob[] = [
  {
    id: 'tsk_mock_stopping',
    title: 'Add dark mode to the settings page',
    role: 'worker',
    state: 'working',
    usage: [12, 1.4, 2.0],
    startedMinAgo: 22,
    cancelRequested: true,
  },
];

/** The 20-machine set's fleet-19, not responding: its jobs as last known, no usage. */
export const LAST_KNOWN_JOBS: readonly SeedJob[] = [
  {
    id: 'tsk_mock_stale_a',
    title: 'Translate onboarding emails to Spanish',
    role: 'worker',
    state: 'last_known',
    startedMinAgo: 50,
  },
  {
    id: 'tsk_mock_stale_b',
    title: 'Reviewing PR #2839',
    role: 'reviewer',
    state: 'last_known',
    startedMinAgo: 35,
  },
];
